import { translate } from '@ngneat/transloco';
import { QueryBuilderConfig, RuleSet } from 'ng-query-builder';
import { Subject } from 'rxjs';

import { CommonUtilsService } from '../services/commonutils/common-utils.service';
import { UtilsService } from '../services/utils/utils.service';
import { Entity } from './entity.class';
import { Field } from './field.class';
import { RecordField } from './record/recordfield.class';
import { EntityFilterCriterias, FilterCriteria, Restriction, SearchRule } from './searchcriteria.global';
export class Search {
    public isSubEntityVisible = [];

    private searchFields: SearchField[] = [];

    public searchConfig: QueryBuilderConfig = {
        fields: {},
    };

    public searchQuery: {
        condition?: string;
        rules?: any[];
    } = {};

    public sortConfig: QueryBuilderConfig = {
        fields: {},
    };

    public sortQuery: {
        condition: string;
        rules: any[];
    };

    public selectedSource: SearchSource;

    public relationQuery: RuleSet = {
        condition: 'and',
        rules: [],
    };

    relationConfig: QueryBuilderConfig = {
        fields: {
            Criteria: {
                name: 'Criteria',
                nullable: false,
                type: 'string',
            },
        },
    };

    private relation: any[];

    public readonly filterCriteria: FilterCriteria = {
        serviceId: undefined,
        entityFilterCriterias: [],
        criteriaRelations: {},
    };

    public criteriaUpdated?: boolean;

    public relationsUpdated?: boolean;

    public searchQueryConfigChange?: Subject<{
        searchConfig?: QueryBuilderConfig;
        sortConfig?: QueryBuilderConfig;
    }> = new Subject();

    public relationQueryConfigChange?: Subject<{
        searchConfig?: QueryBuilderConfig;
        sortConfig?: QueryBuilderConfig;
    }> = new Subject();

    private radioLevelRetainedRules: any[];

    public entityClicked: boolean;

    public needSourceStructure?: boolean;

    public queryBuilderShouldVisible = false;

    public sourceClicked?: boolean;

    public origin?: 'PROCESS' | 'PROCESSES' | 'ENINVOICE' | 'ENCOLLAB';

    public isSortConfigEmpty?: boolean;

    public sources?: SearchSource[];

    public allowRuleset?: boolean;

    public isAddRuleVisible?: boolean;

    public allowCollapse?: boolean;

    public disabled?: boolean;

    public showSearchCriteriaTitle = true;

    public showSortCriteriaTitle = true;

    public sortCriteria: { [property: string]: any } = {};

    public inEditFilterMode: boolean;

    public getOutputSources = (entities: Entity[], criteria?: FilterCriteria) =>
        Search.getOutputSources(entities, criteria || this.filterCriteria);

    private perFieldOptions: {
        [property: string]: {
            name: string;
            value: string;
        }[];
    } = {};

    public dontAllowEmptyRule = false;

    private fieldIdsMap: { [property: string]: SearchField } = {};

    /**
     * @param useRawOperators  When true, operators such as 'NOT_EQUALS' are kept as-is during
     *                         de-optimisation instead of being converted to their display equivalents
     *                         (e.g. '!='). Also controls whether the 'IN' operator treats values as
     *                         raw arrays for select/option fields. Pass true when the consumer's
     *                         operator list already uses the raw API values (e.g. Initiate Action).
     * @param excludeSystemFields  When true, system-level fields (requestId, instanceState.raw,
     *                             instanceStatus.raw) are NOT
     *                             appended to the search configuration. Pass true when the target
     *                             entity does not expose these system fields (e.g. Initiate Action).
     * @param fieldOptions     Pre-defined option list used when building the query-builder config
     *                         for fields that render as dropdowns. Overrides per-field options when
     *                         provided (used by consumers that supply a flat options array).
     */
    constructor(
        private _utils: UtilsService,
        public serviceId?: string,
        public useRawOperators = false,
        public excludeSystemFields = false,
        public fieldOptions?: {
            name: string;
            value: string;
        }[]
    ) {
        this.setCriteria();
    }

    public setCriterias = (
        filterCriteria?: FilterCriteria,
        sortCriteria?: { [property: string]: any },
        skipBuildAutoRelations?: boolean
    ) => {
        this.setCriteria(filterCriteria);
        const deOptimizedCriteria = this.deOptimizeCriteria();
        deOptimizedCriteria?.entityFilterCriterias?.[0]?.entityUid &&
            this.setSource(deOptimizedCriteria.entityFilterCriterias[0].entityUid);
        this.clearSearchQueries(filterCriteria || deOptimizedCriteria, sortCriteria, true, skipBuildAutoRelations);
    };

    private setCriteria = (criteria?: FilterCriteria) => {
        criteria = criteria || {
            serviceId: this.serviceId,
            entityFilterCriterias: [],
            criteriaRelations: {},
        };
        this.removeCustomRules(criteria);
        this.setFilterCriteria(criteria);
    };

    private setSortCriteria = (query?: { [property: string]: any }) => {
        this.sortCriteria = CommonUtilsService.cloneObject(query || {});
    };

    private deOptimizeCriteria = () => {
        const criteria: FilterCriteria = CommonUtilsService.cloneObject(this.filterCriteria);
        if (!this.inEditFilterMode) {
            criteria?.criteriaRelations?.condition &&
                (criteria.criteriaRelations.condition = criteria?.criteriaRelations?.condition.toLowerCase());
        }
        criteria?.criteriaRelations?.rules?.forEach(this.deOptimizeRule);
        criteria?.entityFilterCriterias?.forEach(criteria => {
            criteria.criteriaDefinitions?.forEach(definition => {
                if (!this.inEditFilterMode) {
                    definition?.restriction?.condition &&
                        (definition.restriction.condition = definition?.restriction?.condition.toLowerCase());
                }
                definition.restriction?.rules?.forEach(this.deOptimizeRule);
            });
        });
        return criteria;
    };

    private deOptimizeRule = (rule: SearchRule) => {
        if (rule.fieldId) {
            rule['field'] = rule.fieldId;
        } else if (rule['field']) {
            rule.fieldId = rule['field'];
        }
        if (rule.operator) {
            delete rule.restrictionType;
        }
        if (rule.operator === 'CONTAINS') {
            if (rule.value?.indexOf('*') > -1) {
                rule.value = (rule.value as string).replace(/\*/gm, '');
            }
        } else if (rule.operator === 'NOT_EXISTS') {
            rule.operator = 'is null';
            delete rule.value;
        } else if (rule.operator === 'EXISTS') {
            rule.operator = 'is not null';
            delete rule.value;
        } else if (rule.operator === 'NOT_EQUALS' && !this.useRawOperators) {
            // When useRawOperators is false the operator list uses the display form '!='.
            // Convert the stored API value back so the query-builder renders correctly.
            rule.operator = '!=';
        }
        const fieldMeta = this.fieldIdsMap?.[rule['field']];
        const fieldDataType = fieldMeta?.datatype || this.searchConfig?.fields?.[rule['field']]?.type;
        if (fieldDataType?.toLowerCase() === 'date') {
            const targetFormat = fieldMeta?.format || this.searchConfig?.fields?.[rule['field']]?.['format'] || 'dd-mm-yyyy';
            if (rule.value) {
                if (rule.value instanceof Date) {
                    rule.value = CommonUtilsService.transformDate(rule.value as any, 'DATE', targetFormat as any);
                } else if (typeof rule.value === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(rule.value)) {
                    if (targetFormat.toLowerCase() !== 'dd-mm-yyyy') {
                        rule.value = CommonUtilsService.transformDate(rule.value as any, 'dd-mm-yyyy', targetFormat as any);
                    }
                }
            }
        }
        if ((rule as any).condition) {
            (rule as any).condition = (rule as any).condition.toLowerCase();
        }
        (rule as any).rules?.forEach?.(this.deOptimizeRule);
    };

    private onCriteriaChange = (criteria: FilterCriteria, dontAddEmptyRule?: boolean) => {
        this.setFilterCriteria(criteria);
        if (this.filterCriteria?.entityFilterCriterias?.length > 0) {
            this.createRelationModel(dontAddEmptyRule);
            this.appendToRelationCriteria();
        } else {
            this.relationQuery = {
                condition: 'and',
                rules: [],
            };
            this.relationConfig = {
                fields: {
                    Criteria: {
                        name: 'Criteria',
                        nullable: false,
                        type: 'string',
                    },
                },
            };
        }
    };

    private setFilterCriteria = (criteria: FilterCriteria) => {
        Object.assign(this.filterCriteria, {
            ...this.filterCriteria,
            ...criteria,
        });
    };

    public optimizeCriteria = () => {
        const criteria: FilterCriteria = CommonUtilsService.cloneObject(this.filterCriteria);
        criteria.criteriaRelations?.rules?.forEach(this.optimizeRule);
        if (!this.inEditFilterMode) {
            criteria?.criteriaRelations?.condition &&
                (criteria.criteriaRelations.condition = criteria?.criteriaRelations?.condition.toUpperCase());
        }
        criteria.entityFilterCriterias?.forEach(criteria => {
            criteria.criteriaDefinitions?.forEach(definition => {
                if (!this.inEditFilterMode) {
                    definition?.restriction?.condition &&
                        (definition.restriction.condition = definition?.restriction?.condition.toUpperCase());
                }
                definition.restriction?.rules?.forEach(this.optimizeRule);
            });
        });
        return criteria;
    };

    private optimizeRule = (rule: SearchRule) => {
        delete rule['field'];
        delete (rule as any).entity;
        const sourceField = this.selectedSource?.fields?.find(field => field.id === rule.fieldId);
        if (rule.operator) {
            rule.restrictionType = 'SingleRestrictionMetadata';
        }
        if (rule.operator === 'EQ') {
            switch (sourceField?.datatype) {
                case 'DATE':
                    break;
            }
        } else if (rule.operator === 'CONTAINS') {
            if (rule.value?.indexOf('*') === -1) {
                rule.value = `*${rule.value}*`;
            }
        } else if (rule.operator === 'is null') {
            rule.operator = 'NOT_EXISTS';
            delete rule.value;
        } else if (rule.operator === 'is not null') {
            rule.operator = 'EXISTS';
            delete rule.value;
        } else if (rule.operator === '!=') {
            rule.operator = 'NOT_EQUALS';
        } else if (rule.operator === 'IN') {
            if (this.useRawOperators) {
                // When useRawOperators is true, select/option field values are already stored as arrays;
                // other field values should be kept as-is (no split).
                const selectFieldIds = ['instanceState.raw', 'instanceStatus.raw', 'validationStatus'];
                rule.value = selectFieldIds.includes(rule.fieldId) ? rule.value : `${rule.value}`;
            } else if (!Array.isArray(rule.value)) {
                rule.value = rule.value.split(',') as any;
            }
        }
        if ((rule as any).condition) {
            (rule as any).condition = (rule as any).condition.toUpperCase();
        }
        (rule as any).rules?.forEach?.(this.optimizeRule);
    };

    public clearSearchQueries = (
        criteria?: FilterCriteria,
        sort?: Search['sortCriteria'],
        dontAddEmptyRule?: boolean,
        skipBuildAutoRelations?: boolean
    ) => {
        criteria = criteria || {
            serviceId: this.serviceId,
            entityFilterCriterias: [],
            criteriaRelations: {},
        };
        sort = sort || {};
        this.onCriteriaChange(criteria, dontAddEmptyRule);
        this.setSortCriteria(sort);
        this.setQueriesToDefault();
        this.buildQuerries();
        this.criteriaUpdated = this.buildCriterias(true, true, skipBuildAutoRelations) !== false;
    };

    private getSource = (sources: SearchSource[], sourceId: string) => {
        let source: SearchSource;
        sources?.forEach(item => {
            if (!source) {
                if (item.id === sourceId) {
                    source = item;
                } else if (item.sources?.length > 0) {
                    source = this.getSource(item.sources, sourceId);
                }
            }
        });
        return source;
    };

    public setSource = (sourceId?: string) => {
        sourceId = sourceId || this.selectedSource?.id;
        if (!sourceId) {
            return;
        }
        // Reset queries to default state
        this.setQueriesToDefault();

        // Set the new source
        this.selectedSource = this.getSource(this.sources, sourceId);

        // Reset search fields and configurations
        this.searchFields = [];
        this.searchConfig = {
            fields: {},
        };
        this.sortConfig = {
            fields: {},
        };

        // Create new search model with the new source
        this.createSearchModel();

        // Build queries for the new source
        this.appendToRuleCriteria();
        this.buildSortQuery();

        // Notify subscribers that the configuration has changed
        this.searchQueryConfigChange.next({
            searchConfig: this.searchConfig,
            sortConfig: this.sortConfig,
        });

        // Add an empty rule if needed
        if (this.searchQuery.rules.length === 0 && !this.dontAllowEmptyRule) {
            this.appendEmptyRule();
        }
    };

    public filterSources = (sources: SearchSource[]) => {
        const splitCriteria = JSON.stringify(this.filterCriteria).split('"');
        this.checkSources(sources, splitCriteria, []);
    };

    private checkSources = (sources: SearchSource[], splitCriteria: string[], entitiesList: string[]) => {
        let result: boolean;
        let i = sources.length - 1;
        while (i >= 0) {
            const entity = sources[i];
            const foundInSubEntities = this.checkSources(entity.sources, splitCriteria, entitiesList);
            if (!entitiesList.includes(sources[i].id)) {
                if (foundInSubEntities === false || foundInSubEntities === undefined) {
                    result = false;
                    entity.sources = [];
                } else {
                    entitiesList.push(sources[i].id);
                    result = true;
                }
            }
            result = result || this.checkEntity(entity, splitCriteria);
            if (!result) {
                sources.splice(i, 1);
            } else {
                entitiesList.push(sources[i].id);
            }
            i--;
        }
        return result;
    };

    private checkEntity = (source: SearchSource, splitCriteria: string[]) => {
        return splitCriteria.indexOf(source.id) > -1;
    };

    private createSearchModel = () => {
        this.searchFields = [];
        const source = this.selectedSource;
        source?.fields
            .filter(
                field =>
                    this.origin !== 'ENINVOICE' ||
                    (this.origin === 'ENINVOICE' &&
                        ['api_action', 'conslidate_ewaybill_generation', 'confirmed_by', 'receiver_name', 'attach_gnr'].indexOf(
                            field.id
                        ) === -1)
            )
            .forEach(field => {
                if (field.searchable) {
                    const newField: SearchField = {
                        id: field.id,
                        value: undefined,
                        searchable: field.searchable,
                        name: field.name,
                        sortable: field.sortable,
                        datatype: field.datatype,
                        uiTypeMetadata: field.uiTypeMetadata || (field as any).ui_type_metadata,
                        format: field.format,
                    };
                    if (newField.id === 'e_waybill_status') {
                        newField.value = undefined;
                    }
                    this.searchFields.push(newField);
                }
            });
        if (source?.primary) {
            [
                {
                    displayName: translate('Transmission Id'),
                    id: 'requestId',
                    datatype: 'string',
                },
                {
                    displayName: translate('Instance State'),
                    id: 'instanceState.raw',
                    dataType: 'options',
                },
                {
                    displayName: translate('Workflow Status'),
                    id: 'instanceStatus.raw',
                    datatype:
                        this.perFieldOptions['instanceStatus.raw'] && this.perFieldOptions['instanceStatus.raw'].length > 0
                            ? 'options'
                            : 'string',
                },
                {
                    displayName: translate('Validation Status'),
                    id: 'validationStatus',
                    datatype: 'options',
                },
            ].forEach(field => {
                field['name'] = field.displayName;
                field['searchable'] = true;
                const recordField: SearchField = {
                    id: field?.id,
                    name: field['name'],
                    value: undefined,
                    searchable: field['searchable'],
                    sortable: false,
                    datatype: field.dataType,
                    uiTypeMetadata: (field as any).uiTypeMetadata,
                    format: undefined,
                };
                this.searchFields.push(recordField);
            });
        }
        this.fetchSearchFeilds();
        this.searchQueryConfigChange.next({ searchConfig: this.searchConfig, sortConfig: this.sortConfig });
    };

    private fetchSearchFeilds = () => {
        const source = this.selectedSource;
        const searchObj = {};
        const sortObj = {};
        this.searchFields.forEach(field => {
            if (field.searchable) {
                CommonUtilsService.getElasticSearchObject(
                    field,
                    searchObj,
                    this.fieldOptions !== undefined ? this.fieldOptions : this.perFieldOptions?.[field.id],
                    this.useRawOperators
                );
            }
            if (field.sortable) {
                CommonUtilsService.getElasticSortObject(field, sortObj);
            }
        });
        this.addCriteriaFieldConfigs(source, searchObj);
        this.searchConfig['fields'] = Object.assign({}, searchObj);
        this.sortConfig['fields'] = Object.assign({}, sortObj);
        Object.keys(this.sortConfig.fields).length === 0 && (this.isSortConfigEmpty = true);
        // System-level field overrides only apply when system fields are not excluded.
        // Consumers that set excludeSystemFields=true (e.g. Initiate Action) target a custom
        // business app that does not expose requestId / instanceState.raw / instanceStatus.raw.
        // Instance State is also exposed as a regular field under "General details" of the target app,
        // so its fixed option list must be applied whenever the field is present, even when system fields are excluded.
        if (source?.primary || this.excludeSystemFields) {
            this.searchConfig.fields['instanceState.raw'] && (this.searchConfig.fields['instanceState.raw'].type = 'category');
            this.searchConfig.fields['instanceState.raw'] &&
                (this.searchConfig.fields['instanceState.raw'].options = CommonUtilsService.INSTANCE_STATES);
            this.searchConfig.fields['instanceState.raw'] && (this.searchConfig.fields['instanceState.raw'].nullable = false);
        }
        // Under excludeSystemFields (Initiate Action) Transmission Id stays a category field so the
        // trigger-app / business-key selection can be used.
        if (source?.primary && !this.excludeSystemFields) {
            this.searchConfig.fields['requestId'] && (this.searchConfig.fields['requestId'].type = 'string');
        }
        if (source?.primary || this.excludeSystemFields) {
            if (this.searchConfig.fields['instanceStatus.raw']) {
                this.searchConfig.fields['instanceStatus.raw'].type =
                    this.perFieldOptions?.['instanceStatus.raw']?.length > 0 ? 'category' : 'string';
                this.searchConfig.fields['instanceStatus.raw'].operators = ['EQ', 'IN'];
                this.searchConfig.fields['instanceStatus.raw'].options = this.perFieldOptions?.['instanceStatus.raw'];
            }
        }
        // Validation Status and Created By / Updated By are available on every entity (as in 4.0.5), not only the primary one.
        this.searchConfig.fields['validationStatus'] = {
            name: translate('Validation Status'),
            nullable: false,
            type: 'category',
            options: CommonUtilsService.VALIDATION_STATUS,
        };
        this.searchConfig.fields['createdBy.raw'] = {
            name: translate('Created By / Updated By'),
            type: 'string',
        };
        if (Object.keys(this.searchConfig.fields).length) {
            this.queryBuilderShouldVisible = true;
        } else {
            this.queryBuilderShouldVisible = false;
        }
    };

    // Add field configs for fieldIds referenced by the selected entity's stored criteria,
    // so rules still render when a field is hidden/non-searchable in the metadata
    private addCriteriaFieldConfigs = (source: SearchSource, searchObj: { [fieldId: string]: any }) => {
        this.filterCriteria?.entityFilterCriterias
            ?.filter(criteria => criteria.entityUid === source?.id)
            ?.forEach(criteria =>
                criteria.criteriaDefinitions?.forEach(definition => this.addFieldConfigFromRules(definition?.restriction?.rules, searchObj))
            );
    };

    private addFieldConfigFromRules = (rules: any[], searchObj: { [fieldId: string]: any }) => {
        rules?.forEach(rule => {
            if (rule?.rules?.length) {
                this.addFieldConfigFromRules(rule.rules, searchObj);
                return;
            }
            const fieldId = rule?.fieldId || rule?.field;
            if (!fieldId || searchObj[fieldId]) {
                return;
            }
            const field = this.fieldIdsMap?.[fieldId];
            CommonUtilsService.getElasticSearchObject(
                field || { id: fieldId, name: fieldId, datatype: 'string', uiTypeMetadata: 'TEXTFIELD' },
                searchObj,
                this.fieldOptions !== undefined ? this.fieldOptions : this.perFieldOptions?.[fieldId],
                this.useRawOperators
            );
        });
    };

    private setQueriesToDefault = () => {
        // Reset search query with empty rules array to ensure no previous rules remain
        this.searchQuery = {
            condition: 'and',
            rules: [],
        };

        // Reset sort query with empty rules array
        this.sortQuery = {
            condition: 'and',
            rules: [],
        };
    };

    public buildQuerries = () => {
        this.buildSearchQuery();
        this.buildSortQuery();
    };

    public buildSearchQuery = () => {
        const filterCriteria = this.deOptimizeCriteria();
        const entityCriteria = filterCriteria?.entityFilterCriterias?.[0]?.entityUid;
        const entityExists = this.sources?.find(source => source.id === entityCriteria);
        const entity = this.selectedSource || entityExists || this.sources?.[0];
        const criteria = entity?.id && filterCriteria?.entityFilterCriterias?.find(criteriaItem => criteriaItem.entityUid === entity.id);
        const definitions = criteria?.criteriaDefinitions;

        // Process rules to ensure field IDs are properly set
        definitions?.[0]?.restriction?.rules?.forEach(rule => {
            rule['field'] = rule.fieldId || rule['id'];
        });

        const rules = this.searchFields?.[0]?.id
            ? [
                  {
                      operator: 'EQ',
                      value: undefined,
                      fieldId: this.searchFields?.[0]?.id,
                      field: this.searchFields?.[0]?.id,
                      restrictionType: 'SingleRestrictionMetadata',
                  },
              ]
            : [];
        const newQuery = (definitions && CommonUtilsService.cloneObject(definitions?.[0]?.restriction)) || {
            condition: 'and',
            rules,
        };

        // Validate that all fields in the query exist in the current configuration
        if (newQuery.rules && newQuery.rules.length > 0) {
            this.validateQueryRules(newQuery.rules);
        }

        this.searchQuery = newQuery;
    };

    // Validate and clean up rules to ensure they only reference fields that exist in the current configuration
    private validateQueryRules = (rules: any[]) => {
        if (!rules || !Array.isArray(rules)) return;

        // Filter out rules with fields that don't exist in the current configuration
        for (let i = rules.length - 1; i >= 0; i--) {
            const rule = rules[i];

            // If it's a nested ruleset, validate its rules recursively
            if (rule.rules && rule.rules.length > 0) {
                this.validateQueryRules(rule.rules);
                // If all nested rules were removed, remove this ruleset too
                if (rule.rules.length === 0) {
                    rules.splice(i, 1);
                }
                continue;
            }

            // Check if the field exists in the current configuration
            const fieldId = rule.field || rule.fieldId;
            if (fieldId && !this.searchConfig.fields[fieldId]) {
                // Remove rules with fields that don't exist in the current configuration
                rules.splice(i, 1);
            }
        }
    };

    public buildSortQuery = (query?: { [property: string]: any }) => {
        query = query || this.sortCriteria;
        !this.sortQuery && (this.sortQuery = { condition: 'and', rules: [] });
        this.sortQuery.rules.splice(0);
        Object.keys(query || {})
            .filter(key => this.sortConfig.fields?.[key])
            .forEach(key => {
                this.sortQuery.rules.push({
                    field: key,
                    fieldId: key,
                    operator: '=',
                    value: query[key],
                    restrictionType: 'SingleRestrictionMetadata',
                });
            });
    };

    private appendToRuleCriteria = () => {
        const rulesClone = this.optimizeCriteria()?.entityFilterCriterias;
        const rulesToAppend = [];
        this.radioLevelRetainedRules = [];
        if (rulesClone?.length) {
            rulesClone.forEach(entityFilter => {
                entityFilter.criteriaDefinitions.forEach(criteriaDefinition => {
                    rulesToAppend.push(criteriaDefinition);
                });
            });
            this.radioLevelRetainedRules = rulesToAppend;
            const findSelectedRadioEntity = this.radioLevelRetainedRules.find(rule => rule.criteriaName === this.selectedSource?.id);
            let selectedRuleEntityRestriction = findSelectedRadioEntity?.restriction;
            if (selectedRuleEntityRestriction?.rules.length) {
                selectedRuleEntityRestriction = this.transformRestrictionForRendering(selectedRuleEntityRestriction, this.searchConfig);
                this.checkForLegacyFields(selectedRuleEntityRestriction);
                const hasContains: boolean = selectedRuleEntityRestriction.rules.map(rule => rule.operator).includes('CONTAINS');
                if (hasContains) {
                    this.detatchServerExtras(selectedRuleEntityRestriction, { operator: 'CONTAINS' });
                }
                this.searchQuery = this.transformBooleanToLowerCase(selectedRuleEntityRestriction);
            }
        }
    };

    private checkForLegacyFields = restriction => {
        const checkForExistance = restriction.rules.find(rule => rule.id === 'instanceStatus');
        if (checkForExistance) {
            this.pushWorkflowField('instanceStatus');
        }
    };

    private pushWorkflowField = (fieldId: string) => {
        this.searchConfig.fields[fieldId] = {
            name: 'Workflow Status',
            type: 'string',
            operators: ['EQ', 'IN'],
        };
    };

    private transformRestrictionForRendering = (restriction: any, searchConfig) => {
        if (!restriction || !restriction.rules || !restriction.rules.length) {
            return;
        }
        restriction.rules.forEach(rule => {
            if (rule.operator) {
                rule['field'] = rule.fieldId || rule.id;
                if (rule.operator === 'EXISTS') {
                    // It is a plain rule
                    rule.operator = 'is not null';
                } else if (rule.operator === 'NOT_EXISTS') {
                    // It is a plain rule
                    rule.operator = 'is null';
                }
                if (this.getDataTypeBySearchConfig(rule.field, searchConfig) === 'date') {
                    const targetFormat =
                        this.searchConfig?.fields?.[rule['field']]?.['format'] || this.fieldIdsMap?.[rule['field']]?.format || 'dd-mm-yyyy';
                    if (rule.value) {
                        if (rule.value instanceof Date) {
                            rule.value = CommonUtilsService.transformDate(rule.value, 'DATE', targetFormat as any);
                        } else if (typeof rule.value === 'string' && /^\d{2}-\d{2}-\d{4}$/.test(rule.value)) {
                            if (targetFormat.toLowerCase() !== 'dd-mm-yyyy') {
                                rule.value = CommonUtilsService.transformDate(rule.value, 'dd-mm-yyyy', targetFormat as any);
                            }
                        }
                    }
                }
            } else if (rule.condition) {
                rule = this.transformRestrictionForRendering(rule, searchConfig);
            }
        });
        delete restriction.restrictionType;
        return restriction;
    };

    private getDataTypeBySearchConfig = (fieldName, searchConfig) => {
        return (searchConfig && searchConfig.fields[fieldName] && searchConfig.fields[fieldName].type) || 'string';
    };

    private detatchServerExtras = (restriction, extras: { [property: string]: string }) => {
        switch (extras.operator) {
            case 'CONTAINS':
                if (restriction.rules?.length) {
                    restriction.rules.forEach(rule => {
                        const isTruthy = rule.value?.includes('*');
                        if (isTruthy) {
                            rule.value = rule.value?.slice(1, -1);
                        }
                    });
                }
                break;
            default:
                break;
        }
    };

    private appendEmptyRule = () => {
        const fieldId = this.searchFields?.[0]?.id;
        const rule = fieldId && {
            field: fieldId,
            operator: 'EQ',
            value: undefined,
        };
        const emptyRuleObject = {
            condition: 'and',
            rules: rule ? [rule] : [],
        };
        this.searchQuery = emptyRuleObject as {
            condition: string;
            rules: {
                field: any;
                operator: string;
                value: any;
            }[];
        };
    };

    public createRelationModel = (dontAddEmptyRule: boolean) => {
        this.relation = [];
        const field = {
            displayName: translate('Criteria'),
            name: 'Criteria',
            value: '',
            datatype: 'category',
            id: 'Criteria',
        };
        this.relation.push(field);
        this.fetchRelationFields(field);
        if (this.relationQuery.rules.length === 0 && !dontAddEmptyRule) {
            this.appendEmptyRelation();
        }
        this.relationQueryConfigChange.next({ searchConfig: this.relationConfig });
    };

    private fetchRelationFields = field => {
        const relationObj = {};
        const id = field.id;
        relationObj[id] = {
            name: field.displayName,
            type: 'category',
            operators: ['EQ'],
            nullable: false,
            options: [],
            value: '',
        };
        const criteria = this.deOptimizeCriteria();
        criteria.entityFilterCriterias.forEach(criteria => {
            relationObj[id]['options'].push({
                name: criteria.entityUid,
                value: criteria.entityUid,
            });
        });
        this.relationConfig['fields'] = Object.assign({}, relationObj);
    };

    private appendEmptyRelation = () => {
        const emptyRuleObject = {
            condition: 'and',
            rules: [
                {
                    field: this.relation[0].id,
                    operator: 'EQ',
                    value: undefined,
                },
            ],
        };
        this.relationQuery = emptyRuleObject as any;
    };

    public buildCriterias = (validation?: boolean, silent?: boolean, skipBuildAutoRelations?: boolean) => {
        let hasErrors = false;
        hasErrors = !this.buildSortCriteria(silent) || hasErrors;
        hasErrors = !this.buildSearchCriteria(validation, hasErrors, silent, skipBuildAutoRelations) || hasErrors;
        return !hasErrors;
    };

    public buildSortCriteria = (silent?: boolean) => {
        const sort: {
            [property: string]: string;
        } = {} as const;
        this.correctQuery(this.sortQuery);
        if (!this.isValidCriteria(this.sortQuery, 'SORT')) {
            !silent && this._utils.alertError(translate('Sort criteria is not valid'));
            return false;
        }
        this.sortQuery?.rules?.forEach(rule => {
            sort[rule.field] = rule.value;
        });
        this.setSortCriteria(sort);
        return sort;
    };

    private getRulesLength = (criteria: FilterCriteria) => {
        let length = 0;
        criteria.entityFilterCriterias
            ?.filter(entity => entity.entityUid !== this.selectedSource?.id)
            ?.forEach(entity => {
                entity.criteriaDefinitions?.forEach(definition => {
                    length += definition.restriction.rules.length;
                });
            });
        return length;
    };

    private removeEmptyRules = (rules: Search['searchQuery']['rules']) => {
        rules.forEach((rule, index) => {
            if (rule.rules?.length > 0) {
                this.removeEmptyRules(rule.rules);
                rule.rules.length === 0 && rules.splice(index, 1);
            } else if (rule.operator !== 'is not null' && rule.operator !== 'is null' && this._utils.isEmpty(rule.value)) {
                rules.splice(index, 1);
            }
        });
    };

    public buildSearchCriteria = (validation?: boolean, hasErrors?: boolean, silent?: boolean, skipBuildAutoRelations?: boolean) => {
        const updated = this.criteriaUpdated;
        this.correctQuery(this.searchQuery);
        const restriction: Search['searchQuery'] = CommonUtilsService.cloneObject(this.searchQuery);
        let dontCheckOnRestriction = false;
        const otherEntitiesRulesLength = this.getRulesLength(this.filterCriteria);
        if (validation !== undefined && otherEntitiesRulesLength > 0) {
            this.removeEmptyRules(restriction.rules);
            restriction.rules.length === 0 && (dontCheckOnRestriction = true);
        } else if ((!restriction.rules || restriction.rules.length === 0) && otherEntitiesRulesLength > 0) {
            this.removeEmptyRules(restriction.rules);
            dontCheckOnRestriction = true;
        } else if (!restriction.rules || restriction.rules.length === 0) {
            !silent && this._utils.alertError(translate('Search criteria is not valid'));
            this.updateSearchCriteria(this.filterCriteria, this.selectedSource, dontCheckOnRestriction);
            return false;
        }
        const isINArray = restriction.rules.length > 0 && Array.isArray(restriction.rules[0].value);
        const isValidINRule = this.isValidINRule(restriction);
        if (
            (!dontCheckOnRestriction && !restriction.rules.length) ||
            (!dontCheckOnRestriction && !restriction.rules.length && !this.filterCriteria.entityFilterCriterias.length) ||
            (isINArray && isValidINRule === 0)
        ) {
            !silent && this._utils.alertError(translate('Search criteria cannot be empty'));
            return false;
        } else if (restriction.rules.length === 0 && this.filterCriteria.entityFilterCriterias.length > 0) {
            const value = this.updateSearchCriteria(this.filterCriteria, this.selectedSource, dontCheckOnRestriction);
            if (value) {
                this.criteriaUpdated = true;
                this.selectedSource?.name &&
                    this._utils.alertCustomSuccess(`${this.selectedSource.name} ${translate('Criteria has been updated')}`);
                const criteriaRelations: Restriction =
                    this.filterCriteria.criteriaRelations?.rules?.length > 0
                        ? {
                              ...this.filterCriteria.criteriaRelations,
                              rules: [],
                          }
                        : {
                              restrictionType: 'JoinedRestrictionMetadata',
                          };
                this.onCriteriaChange(
                    {
                        ...this.filterCriteria,
                        criteriaRelations: criteriaRelations,
                    },
                    true
                );
                !skipBuildAutoRelations && this.autoTriggerRelations();
                return true;
            }
        } else if (!dontCheckOnRestriction && !this.isValidCriteria(restriction)) {
            !silent && this._utils.alertError(translate('Search criteria is not valid'));
            return false;
        } else if (!dontCheckOnRestriction) {
            this.applyRecurssionToRules(restriction, this.searchConfig);
            const criteriaDefinition = {
                type: 'CriteriaDefinition',
                criteriaName: this.selectedSource.id,
                restriction,
            };
            const entityFilterCriteria = {
                entityUid: this.selectedSource.id,
                criteriaDefinitions: [],
            };
            entityFilterCriteria.criteriaDefinitions.push(criteriaDefinition);
            const callBackEntityFilterCriteria = this.checkForExistance(entityFilterCriteria, this.filterCriteria);
            if (typeof callBackEntityFilterCriteria === 'object') {
                this.onCriteriaChange({
                    entityFilterCriterias: [...this.filterCriteria.entityFilterCriterias, callBackEntityFilterCriteria],
                });
            } else {
                const criteria = CommonUtilsService.cloneObject(this.filterCriteria);
                criteria.entityFilterCriterias[callBackEntityFilterCriteria] = entityFilterCriteria;
                this.onCriteriaChange(criteria);
            }
            !validation &&
                !hasErrors &&
                !silent &&
                this._utils.alertCustomSuccess(
                    `${this.selectedSource.name} ${translate('Criteria has been')} ${updated ? translate('updated') : translate('added')}`
                );
            const criteriaRelations =
                this.filterCriteria.criteriaRelations?.rules?.length > 0
                    ? this.filterCriteria.criteriaRelations
                    : {
                          restrictionType: 'JoinedRestrictionMetadata',
                      };
            this.onCriteriaChange({
                ...this.filterCriteria,
                criteriaRelations: criteriaRelations,
            });
            !skipBuildAutoRelations && this.autoTriggerRelations();
            this.criteriaUpdated = true;
            return true;
        }
    };

    private correctQuery = (query: { condition?: string; rules?: SearchRule[] }) => {
        query?.rules?.forEach(rule => {
            if (Array.isArray(rule.value)) {
                rule.value = rule.value.map(value => (typeof value === 'string' ? value.trim() : value));
            } else if (typeof rule.value === 'string') {
                rule.value = rule.value.trim();
            }
        });
    };

    private isValidINRule = (restriction): number => {
        const isArray = Array.isArray(restriction.rules.length && restriction.rules[0].value);
        if (isArray) {
            // When useRawOperators is true, select/option fields store values as arrays;
            // skip the length-check validation for those fields so they are treated as valid.
            if (this.useRawOperators) {
                const selectFieldIds = ['requestId', 'instanceState.raw', 'instanceStatus.raw', 'validationStatus'];
                if (selectFieldIds.includes(restriction.rules[0].field)) {
                    return 1;
                }
            }
            return restriction.rules[0].value.length;
        }
    };

    private updateSearchCriteria = (filterCriteria, selectedSearchEntity: SearchSource, dontCheckOnRestriction: boolean) => {
        const checkForExistance = filterCriteria.entityFilterCriterias?.find(criteria => criteria.entityUid === selectedSearchEntity?.id);
        const checkForRelationExistance = filterCriteria.criteriaRelations?.rules?.find(rule => rule.value === selectedSearchEntity?.id);
        if (checkForRelationExistance) {
            const index = filterCriteria.criteriaRelations.rules.findIndex(rule => rule.value === checkForRelationExistance.value);
            filterCriteria.criteriaRelations.rules.splice(index, 1);
        }
        if (checkForExistance) {
            const index = filterCriteria.entityFilterCriterias.findIndex(criteria => criteria.entityUid === checkForExistance.entityUid);
            filterCriteria.entityFilterCriterias.splice(index, 1);
            return true;
        }
        if ((!checkForExistance && !checkForRelationExistance) || dontCheckOnRestriction) {
            return true;
        }
        this._utils.alertError(translate('Search criteria cannot be empty'));
    };

    private isValidCriteria = (restriction, type?: 'SORT') => {
        let isValid = true;
        if ((!restriction || !restriction.rules || !restriction.rules.length) && type !== 'SORT') {
            isValid = false;
        }
        restriction.rules.forEach(rule => {
            if (rule.operator === 'is not null' || rule.operator === 'is null') {
                return isValid;
            } else {
                if (isValid && rule.rules) {
                    isValid = this.isValidCriteria(rule);
                } else {
                    const ruleValue = typeof rule.value === 'number' ? rule.value.toString() : rule.value;
                    isValid = isValid && ruleValue !== undefined && ruleValue ? true : false;
                }
            }
        });
        return isValid;
    };

    private applyRecurssionToRules = (rule, searchConfig) => {
        if ('rules' in rule) {
            if (rule.collapsed !== 'null' && rule.collapsed !== 'undefined') {
                delete rule.collapsed;
            }
            rule.condition = rule.condition.toUpperCase();
            rule.rules.forEach(innerRule => {
                const field = this.getField(innerRule.field, searchConfig);
                const fieldFormat = field?.format || 'dd-mm-yyyy';
                if (innerRule.field && this.getDataTypeOfField(innerRule.field, searchConfig) === 'date') {
                    if (innerRule.value instanceof Date) {
                        innerRule.value = this._utils.transformDate(innerRule.value, 'DATE', 'dd-mm-yyyy');
                    } else if (typeof innerRule.value === 'string') {
                        if (!/^\d{2}-\d{2}-\d{4}$/.test(innerRule.value)) {
                            innerRule.value = this._utils.transformDate(innerRule.value, fieldFormat as any, 'dd-mm-yyyy');
                        }
                    }
                }
                if (innerRule.operator === 'CONTAINS') {
                    if (innerRule.value.toString().indexOf('*') !== 0) {
                        if (typeof innerRule.value === 'string') {
                            innerRule.value = innerRule.value.trim();
                        }
                        innerRule.value = '*' + innerRule.value + '*';
                    }
                } else if (innerRule.operator === 'is null') {
                    innerRule.operator = 'NOT_EXISTS';
                    delete innerRule.value;
                } else if (innerRule.operator === 'is not null') {
                    innerRule.operator = 'EXISTS';
                    delete innerRule.value;
                } else if (innerRule.operator === '!=') {
                    innerRule.operator = 'NOT_EQUALS';
                }
                this.applyRecurssionToRules(innerRule, searchConfig);
            });
            rule['restrictionType'] = 'JoinedRestrictionMetadata';
        } else {
            rule['fieldId'] = rule['field'];
            rule['restrictionType'] = 'SingleRestrictionMetadata';
            if (rule.collapsed !== 'null' && rule.collapsed !== 'undefined') {
                delete rule.collapsed;
            }
            if (rule?.id) {
                delete rule.id;
            }
            if (rule?.type) {
                delete rule.type;
            }
        }
    };

    private checkForExistance = (
        entityFilterCriteria: {
            entityUid: string;
            criteriaDefinitions: any[];
        },
        filterCriteria: FilterCriteria
    ) => {
        if (!filterCriteria.entityFilterCriterias.find(entityFilter => entityFilter.entityUid === entityFilterCriteria.entityUid)) {
            return entityFilterCriteria;
        } else {
            const entityIndex = filterCriteria.entityFilterCriterias.findIndex(
                entityFilter => entityFilter.entityUid === entityFilterCriteria.entityUid
            );
            return entityIndex;
        }
    };

    private getField = (fieldName: string, searchConfig: { [fieldName: string]: SearchField }): SearchField => {
        return searchConfig?.fields[fieldName];
    };

    private getDataTypeOfField = (fieldName, searchConfig) => {
        return searchConfig?.fields[fieldName]?.type || 'string';
    };

    private autoTriggerRelations = () => {
        const relationRule = this.buildAutoRelationsQuery(this.filterCriteria);
        relationRule && this.onRelationQueryChanged(relationRule);
    };

    private buildAutoRelationsQuery = (filterCriteria: FilterCriteria) => {
        const relations: FilterCriteria['entityFilterCriterias'] = CommonUtilsService.cloneObject(filterCriteria.entityFilterCriterias);
        let relationRule: Restriction;
        if (this.relationQuery.rules?.length > 0) {
            relationRule = CommonUtilsService.cloneObject(this.relationQuery);
        } else if (filterCriteria.criteriaRelations) {
            relationRule = CommonUtilsService.cloneObject(filterCriteria.criteriaRelations);
        } else {
            relationRule = {
                condition: 'and',
                rules: [],
            };
        }
        const noRuleEntityIds: string[] = [];
        relations.forEach(relation => {
            noRuleEntityIds.push(relation.entityUid);
        });
        this.filterRelationRules(relationRule, noRuleEntityIds);
        const entityIds: string[] = [];
        let found: SearchRule;
        relations.forEach(relation => {
            relationRule.rules = relationRule.rules || [];
            relationRule.condition = relationRule.condition || 'and';
            found = this.checkForRule(relationRule.rules, relation.entityUid);
            !found && entityIds.push(relation.entityUid);
        });
        entityIds.forEach(id => {
            const emptyRule = relationRule.rules.find(rule => rule.value === undefined);
            if (emptyRule) {
                emptyRule.value = id;
                return;
            }
            relationRule.rules.push({
                field: 'Criteria',
                operator: 'EQ',
                value: id,
            } as any as SearchRule);
        });
        return relationRule as any as RuleSet;
    };

    private filterRelationRules(relationRule, noRuleEntityIds) {
        relationRule.rules = relationRule.rules.filter(rule => {
            if (typeof rule.value === 'string') {
                return noRuleEntityIds.includes(rule.value);
            } else if (rule.rules && rule.rules.length > 0) {
                this.filterRelationRules(rule, noRuleEntityIds);
                return rule.rules.length > 0;
            }
            return false;
        });
    }

    private checkForRule = (rule: SearchRule | SearchRule[], entityId: string) => {
        let foundRule: SearchRule;
        if (Array.isArray(rule)) {
            rule.forEach(rule => (foundRule = foundRule || this.checkForRule(rule, entityId)));
        } else {
            if (rule.value === entityId) {
                foundRule = rule;
            } else if (rule.hasOwnProperty('rules')) {
                foundRule = foundRule || this.checkForRule(rule['rules'], entityId);
            }
        }
        return foundRule;
    };

    public applyRelations = () => {
        this.onRelationQueryChanged(this.relationQuery, true);
    };

    private onRelationQueryChanged = (data: RuleSet, trigger?: boolean) => {
        const relation = CommonUtilsService.cloneObject(data);
        if (relation.rules.length === 0) {
            this._utils.alertError(translate('Relation criteria cannot be empty'));
            return;
        } else if (!this.isValidCriteria(data)) {
            this._utils.alertError(translate('Relation criteria is not valid'));
            return;
        } else {
            this.relationQuery = data;
            this.applyRecurssionToRules(relation, this.relationConfig);
            const criteriaRelations = {
                ...relation,
                restrictionType: relation.restrictionType || 'JoinedRestrictionMetadata',
            };
            this.onCriteriaChange({
                ...this.filterCriteria,
                criteriaRelations: criteriaRelations,
            });
            if (trigger) {
                this._utils.alertCustomSuccess(translate('Relation has been applied'));
                this.relationsUpdated = true;
            }
        }
    };

    private appendToRelationCriteria = () => {
        const tabLevelRetainedRelations = [];
        let relationsClone: Restriction;
        if (this.relationQuery?.rules?.length > 0) {
            relationsClone = CommonUtilsService.cloneObject(this.relationQuery);
        } else {
            relationsClone = CommonUtilsService.cloneObject(this.filterCriteria.criteriaRelations);
        }
        if (Object.keys(relationsClone || {}).length > 0 && relationsClone.rules?.length > 0) {
            relationsClone.rules.forEach(rule => {
                this.applyRecurssionForRelations(rule);
            });
            delete relationsClone.restrictionType;
            tabLevelRetainedRelations.push(relationsClone);
            this.relationQuery = this.transformBooleanToLowerCase(relationsClone);
        }
    };

    private applyRecurssionForRelations = rule => {
        if ('rules' in rule) {
            rule.rules.forEach(innerRule => {
                this.applyRecurssionForRelations(innerRule);
            });
        } else {
            rule['field'] = rule.field ?? rule.fieldId ?? rule.id;
        }
    };

    private transformBooleanToLowerCase = queryObj => {
        if (!queryObj) {
            return;
        }
        if (queryObj['condition'] === 'AND') {
            queryObj['condition'] = 'and';
        } else if (queryObj['condition'] === 'OR') {
            queryObj['condition'] = 'or';
        }
        if (queryObj.rules && queryObj.rules.length) {
            for (let i = 0; i < queryObj.rules.length; i++) {
                queryObj.rules[i] = this.transformBooleanToLowerCase(queryObj.rules[i]);
            }
        }
        return queryObj;
    };

    public setSources = (sources: SearchSource[]) => {
        this.sources = sources;
        this.createFieldIdsMap(this.sources);
    };

    private createFieldIdsMap = (sources: SearchSource[]) => {
        sources.forEach(source => {
            source.fields.forEach(field => {
                this.fieldIdsMap[field.id] = field;
            });
            source?.sources?.length > 0 && this.createFieldIdsMap(source.sources);
        });
    };

    public setServiceId = (serviceId: string) => {
        this.setFilterCriteria({
            ...this.filterCriteria,
            serviceId: serviceId,
        });
    };

    public convertEntitiesAndSetAsSources = (entities: Entity[], setDefaults?: boolean, skipBuildAutoRelations?: boolean) => {
        const sources = entities
            .filter(entity => entity.show)
            .reduce((sources, entity) => {
                sources.push(this.convertEntity(entity));
                return sources;
            }, [] as SearchSource[]);
        this.setSources(sources);
        setDefaults && this.setPrimaryAsSource();
        setDefaults && this.clearSearchQueries(undefined, undefined, undefined, skipBuildAutoRelations);
    };

    public setPrimaryAsSource = () => {
        const primarySourceId = this.sources?.find(source => source.primary)?.id;
        primarySourceId && this.setSource(primarySourceId);
    };

    public convertEntities = (entities: Entity[], considerAll?: boolean): SearchSource[] => {
        return entities
            .filter(entity => entity.show || considerAll)
            .reduce((sources, entity) => {
                sources.push(this.convertEntity(entity, considerAll));
                return sources;
            }, [] as SearchSource[]);
    };

    public convertEntity = (entity: Entity, considerAll?: boolean): SearchSource => {
        return {
            name: entity.displayName || entity.name,
            fields: this.convertFields(entity.fields, considerAll),
            id: entity.uid || entity['id'],
            primary: entity.primary,
            sources: this.convertEntities(entity.entities || entity['sources'] || [], considerAll),
        };
    };

    private convertFields = (fields: Field[], considerAll?: boolean): SearchField[] => {
        return fields
            .filter(field => field.show || considerAll)
            .reduce((searchFields, field) => {
                searchFields.push(this.convertField(field));
                return searchFields;
            }, [] as SearchField[]);
    };

    private convertField = (field: Field): SearchField => {
        return {
            id: field.uid,
            name: field.displayName || field.name,
            searchable: field.searchable,
            sortable: field.sortable,
            value: field.defaultValue,
            datatype: field.datatype,
            uiTypeMetadata: field.uiTypeMetadata || (field as any).ui_type_metadata || (field.hasLookup ? 'DROPDOWN' : 'TEXTFIELD'),
            format: field.outputFormat || (field as any).format || 'dd-mm-yyyy',
        };
    };

    public removeRequestRule = (criteria?: FilterCriteria) => this.removeCustomRules(criteria, ['requestId']);

    public removeCustomRules = (criteria?: FilterCriteria, customProps?: string[]) => {
        criteria = criteria || this.filterCriteria;
        if (!criteria) {
            return;
        }
        criteria.entityFilterCriterias?.forEach(entityFilterCriteria => {
            let rulesLength = 0;
            entityFilterCriteria.criteriaDefinitions.forEach(criteriaDefinition => {
                ['assetDataId', 'parentRecordId', ...(customProps || [])].forEach(id => {
                    const index = criteriaDefinition.restriction.rules.findIndex(rule => rule.fieldId === id);
                    index > -1 && criteriaDefinition.restriction.rules.splice(index, 1);
                });
                rulesLength = Math.max(rulesLength, criteriaDefinition.restriction.rules.length);
            });
            if (rulesLength === 0) {
                entityFilterCriteria.criteriaDefinitions = [];
                const index = criteria.criteriaRelations.rules.findIndex(rule => rule.value === entityFilterCriteria.entityUid);
                index > -1 && criteria.criteriaRelations.rules.splice(index, 1);
            }
        });
        criteria.entityFilterCriterias = criteria.entityFilterCriterias?.filter(
            filterCriteria => filterCriteria.criteriaDefinitions.length > 0
        );
    };

    private getRuleSource = (entityFilterCriterias: EntityFilterCriterias[], entityId: string) => {
        return entityFilterCriterias?.find(criteria => criteria.entityUid === entityId);
    };

    public filteroutEntities = (criteria: FilterCriteria, entities: string[]) => {
        criteria.criteriaRelations.rules = criteria.criteriaRelations.rules.filter(rule => entities.includes(rule.value as string));
        criteria.entityFilterCriterias = criteria.entityFilterCriterias.filter(criteria => entities.includes(criteria.entityUid));
    };

    public changeRelationToAnd = (criteria: FilterCriteria) => {
        criteria?.criteriaRelations && (criteria.criteriaRelations.condition = 'AND');
    };

    public pushRule = (ruleToPush: SearchRule, criteria: FilterCriteria, entityId: string) => {
        let entityCriteria = this.getRuleSource(criteria.entityFilterCriterias, entityId);
        const previousRestriction = CommonUtilsService.cloneObject(entityCriteria?.criteriaDefinitions?.[0]?.restriction);
        if (!entityCriteria) {
            entityCriteria = {
                entityUid: entityId,
                criteriaDefinitions: [
                    {
                        criteriaName: entityId,
                        restriction: {
                            condition: 'AND',
                            rules: [ruleToPush],
                            restrictionType: 'JoinedRestrictionMetadata',
                        },
                        type: 'CriteriaDefinition',
                    },
                ],
            };
            criteria.entityFilterCriterias.push(entityCriteria);
        } else if (previousRestriction.condition === 'AND') {
            this.checkAndPushRule(entityCriteria.criteriaDefinitions[0].restriction.rules, ruleToPush);
        } else {
            entityCriteria.criteriaDefinitions[0].restriction.rules = [ruleToPush, previousRestriction];
            entityCriteria.criteriaDefinitions[0].restriction.condition = 'AND';
        }
        if (!criteria.criteriaRelations?.rules?.length) {
            const criteriaRelations = {
                condition: 'AND',
                rules: [],
                restrictionType: 'JoinedRestrictionMetadata',
            };
            criteria.entityFilterCriterias.forEach(entityFilterCriteria => {
                criteriaRelations.rules.push({
                    operator: 'EQ',
                    value: entityFilterCriteria.criteriaDefinitions[0].criteriaName,
                    fieldId: 'Criteria',
                    restrictionType: 'SingleRestrictionMetadata',
                });
            });
            criteria.criteriaRelations = criteriaRelations;
        } else {
            const index = criteria.criteriaRelations.rules.find(rule => rule.value === entityId);
            !index &&
                criteria.criteriaRelations.rules.push({
                    operator: 'EQ',
                    value: entityId,
                    fieldId: 'Criteria',
                    restrictionType: 'SingleRestrictionMetadata',
                });
        }
    };

    private checkAndPushRule = (rules: SearchRule[], ruleToPush: SearchRule) => {
        const ruleFound = rules.find(ruleObject => ruleObject.fieldId === ruleToPush.fieldId);
        if (ruleFound) {
            Object.assign(ruleFound, ruleToPush);
        } else {
            rules.push(ruleToPush);
        }
    };

    static combineFieldAndCompoundSearchCriteria = (criteria, fields, entityUid) => {
        criteria.filterCriteria.entityFilterCriterias.forEach(entityCriteria => {
            if (entityCriteria.entityUid === entityUid) {
                fields?.forEach(fld => {
                    const value = Search.getValueByDatatype(fld);
                    const isMultipleValues = Array.isArray(value) && value.length > 1;
                    entityCriteria.criteriaDefinitions[0].restriction.rules.push({
                        fieldId: fld.id,
                        operator: isMultipleValues
                            ? ['DATE', 'BOOLEAN'].includes(fld.datatype)
                                ? 'EQ'
                                : 'IN'
                            : ['INT', 'LONG', 'DOUBLE', 'DECIMAL', 'FLOAT', 'DATE', 'BOOLEAN'].includes(fld.datatype)
                            ? 'EQ'
                            : 'CONTAINS',
                        value: value,
                        restrictionType: 'SingleRestrictionMetadata',
                    });
                });
            }
        });
        return criteria;
    };

    static getRecordsSearchCriteria = (
        restApiName: string,
        serviceId: string,
        instanceId: string,
        arrayEntity: {
            entityId: string;
            parentRecordId: string;
        },
        size?: number,
        fields?: RecordField[],
        isGlobalSearch?: boolean,
        sortField?: string,
        sortDirection?: string
    ) => {
        const hasParentId = arrayEntity.parentRecordId;
        const entityDetailsToPush: EntityFilterCriterias = {
            entityUid: arrayEntity.entityId,
            criteriaDefinitions: [
                {
                    criteriaName: arrayEntity.entityId,
                    restriction: {
                        condition: 'AND',
                        rules: [
                            {
                                fieldId: hasParentId ? 'parentRecordId' : 'assetDataId',
                                operator: 'EQ',
                                value: arrayEntity.parentRecordId || instanceId,
                                restrictionType: 'SingleRestrictionMetadata',
                            },
                        ],
                        restrictionType: 'JoinedRestrictionMetadata',
                    },
                    type: 'CriteriaDefinition',
                },
            ],
        };
        const fieldsRuleBlock = [];
        fields?.forEach(fld => {
            const value = Search.getValueByDatatype(fld);
            const isMultipleValues = Array.isArray(value) && value.length > 1;
            fieldsRuleBlock.push({
                fieldId: fld.id,
                operator: isMultipleValues
                    ? ['DATE', 'BOOLEAN'].includes(fld.datatype)
                        ? 'EQ'
                        : 'IN'
                    : ['INT', 'LONG', 'DOUBLE', 'DECIMAL', 'FLOAT', 'DATE', 'BOOLEAN'].includes(fld.datatype)
                    ? 'EQ'
                    : 'CONTAINS',
                value: value,
                restrictionType: 'SingleRestrictionMetadata',
            });
        });
        if (isGlobalSearch) {
            entityDetailsToPush.criteriaDefinitions[0].restriction.rules.push({
                restrictionType: 'JoinedRestrictionMetadata',
                condition: 'OR',
                rules: fieldsRuleBlock,
            } as any);
        } else {
            entityDetailsToPush.criteriaDefinitions[0].restriction.rules.push(...fieldsRuleBlock);
        }
        return {
            entityId: arrayEntity.entityId,
            restApiServiceName: restApiName,
            filterCriteria: {
                serviceId: serviceId,
                entityFilterCriterias: [entityDetailsToPush],
                criteriaRelations: {
                    condition: 'AND',
                    rules: [
                        {
                            operator: 'EQ',
                            value: arrayEntity.entityId,
                            fieldId: 'Criteria',
                            restrictionType: 'SingleRestrictionMetadata',
                        },
                    ],
                    restrictionType: 'JoinedRestrictionMetadata',
                },
            },
            sort: sortField && sortDirection ? { [sortField]: sortDirection } : {},
            size: size || 20,
        };
    };

    static getRequestSearchCriteria = (restApiName: string, serviceId: string, requestId: string, size = 1, enityUid: string) => {
        const entityDetailsToPush: EntityFilterCriterias = {
            entityUid: enityUid,
            criteriaDefinitions: [
                {
                    criteriaName: enityUid,
                    restriction: {
                        condition: 'AND',
                        rules: [
                            {
                                fieldId: 'requestId',
                                operator: 'EQ',
                                value: requestId,
                                restrictionType: 'SingleRestrictionMetadata',
                            },
                        ],
                        restrictionType: 'JoinedRestrictionMetadata',
                    },
                    type: 'CriteriaDefinition',
                },
            ],
        };
        return {
            entityId: enityUid,
            restApiServiceName: restApiName,
            filterCriteria: {
                serviceId: serviceId,
                entityFilterCriterias: [entityDetailsToPush],
                criteriaRelations: {
                    condition: 'AND',
                    rules: [
                        {
                            operator: 'EQ',
                            value: enityUid,
                            fieldId: 'Criteria',
                            restrictionType: 'SingleRestrictionMetadata',
                        },
                    ],
                    restrictionType: 'JoinedRestrictionMetadata',
                },
            },
            size,
        };
    };

    private static getValueByDatatype = field => {
        let fieldValue: any;
        switch (field.datatype) {
            case 'INT':
            case 'LONG':
            case 'DOUBLE':
            case 'DECIMAL':
            case 'FLOAT':
                fieldValue = field.value.toString();
                return fieldValue.includes(',') ? fieldValue.split(',').map(v => v.trim()) : fieldValue;
            case 'BOOLEAN':
                return field.value;
            case 'DATE':
                return CommonUtilsService.transformDateToLocale(field.value, field.outputFormat, 'dd-mm-yyyy', false);
            default:
                fieldValue = field.value.toString();
                return fieldValue.includes(',') ? fieldValue.split(',').map(v => v.trim()) : `*${fieldValue}*`;
        }
    };

    public filteroutRules = (criteria: FilterCriteria, entities: string[], exemptedFieldIds: string[]) => {
        criteria.entityFilterCriterias = criteria.entityFilterCriterias.reduce((filterCriterias, criteria) => {
            if (entities.includes(criteria.entityUid)) {
                criteria.criteriaDefinitions = criteria.criteriaDefinitions.reduce((definitions, definition) => {
                    definition.restriction.rules = definition.restriction.rules.reduce((rules, rule) => {
                        exemptedFieldIds.includes(rule.fieldId) && rules.push(rule);
                        return rules;
                    }, []);
                    definition.restriction.rules?.length > 0 && definitions.push(definition);
                    return definitions;
                }, []);
            }
            criteria.criteriaDefinitions?.length > 0 && filterCriterias.push(criteria);
            return filterCriterias;
        }, []);
    };

    static getOutputSources = (entities: Entity[], criteria?: FilterCriteria) => {
        if (criteria.outputEntities?.length > 0) {
            return criteria.outputEntities;
        }
        return Search.getSubEntities(entities);
    };

    private static getSubEntities = (entities: Entity[]) => {
        return entities.reduce((entitiesList, entity) => {
            entitiesList.push(entity.uid);
            entity.entities?.length > 0 && entitiesList.push(...this.getSubEntities(entity.entities));
            return entitiesList;
        }, [] as string[]);
    };

    public getSearchFields = () => this.searchFields;

    public pushFieldOptions = (fieldId: string, options: { name: string; value: string }[]) => {
        if (!this.perFieldOptions[fieldId]) {
            this.perFieldOptions[fieldId] = [];
        }
        this.perFieldOptions[fieldId] = options;
        this.setFieldOptions(fieldId);
    };

    private setFieldOptions = (fieldId: string) => {
        if (this.searchConfig.fields?.[fieldId]) {
            const hasOptions = this.perFieldOptions[fieldId] && this.perFieldOptions[fieldId].length > 0;
            this.searchConfig.fields[fieldId] = {
                name: translate('Workflow Status'),
                nullable: false,
                type: hasOptions ? 'category' : 'string',
                options: hasOptions ? this.perFieldOptions[fieldId] : [],
                operators: hasOptions && ['EQ', 'IN'],
            };
            this.searchQueryConfigChange.next({ searchConfig: this.searchConfig, sortConfig: this.sortConfig });
        }
    };

    prepareForPayload = (criteria: FilterCriteria) => {
        criteria?.entityFilterCriterias?.forEach(filter => {
            this.prepareFilterCriteriaForPayload(filter);
        });
    };

    prepareFilterCriteriaForPayload = (filter: EntityFilterCriterias) => {
        filter.criteriaDefinitions?.forEach(criteria => {
            this.prepareRuleForPayload(criteria.restriction?.rules);
        });
    };

    prepareRuleForPayload = (rules: SearchRule[]) => {
        rules?.forEach(rule => {
            if (rule?.['rules']?.length > 0) {
                this.prepareRuleForPayload(rule?.['rules']);
            } else {
                const fieldId = rule.fieldId || rule['field'];
                const value = rule?.value;
                if (Array.isArray(rule.value)) {
                    rule.value = rule.value.map(v => this.prepareFieldForPayload(fieldId, v));
                } else {
                    rule.value = this.prepareFieldForPayload(fieldId, value);
                }
            }
        });
    };

    prepareFieldForPayload = (fieldId: string, value: any) => {
        const field = this.fieldIdsMap?.[fieldId];
        if (field) {
            switch (field.datatype) {
                case 'DATE':
                    // - must occur twice
                    if (!value?.includes('-') || value?.split?.('-')?.length !== 3) {
                        return CommonUtilsService.transformDateToLocale(value, field.format as any, 'dd-mm-yyyy', false);
                    }
                    return value;
                default:
                    return value;
            }
        }
        return value;
    };
}

export interface SearchSource {
    id: string;

    name: string;

    sources?: SearchSource[];

    fields: SearchField[];

    primary: boolean;
}

export interface SearchField {
    id: string;

    value: any;

    searchable: boolean;

    name: string;

    sortable: boolean;

    datatype: string;

    uiTypeMetadata?: string;

    format: string;
}
